/**
 * Director playbook store — makes `director.md` visible + editable from
 * the Koko control panel at runtime.
 *
 * `director.md` is Koko's directing brain. The file ships as the default
 * (imported `?raw` at build time), but the user can edit it live from the
 * Koko panel. Edits can't be written back to disk from the browser, so we
 * persist an OVERRIDE in localStorage and prefer it when present. Reset
 * drops the override and falls back to the shipped file.
 *
 * StudioKokoBridge reads `getEffectiveDirectorPlaybook()` when building the
 * system prompt, so an edit changes how Koko directs WITHOUT a rebuild.
 */

import directorPlaybookDefault from "./director.md?raw";

const STORAGE_KEY = "koko_director_playbook";

type PlaybookListener = (playbook: string) => void;

const listeners = new Set<PlaybookListener>();

/** The shipped `director.md`, trimmed of trailing whitespace. */
export function getDefaultDirectorPlaybook(): string {
  return directorPlaybookDefault.trim();
}

/** True when the user has saved a custom override that differs from default. */
export function isDirectorPlaybookCustomized(): boolean {
  const override = readOverride();
  return override !== null && override.trim() !== getDefaultDirectorPlaybook();
}

/**
 * The playbook Koko actually runs with: the saved override if present,
 * otherwise the shipped default. Always trimmed and non-empty.
 */
export function getEffectiveDirectorPlaybook(): string {
  const override = readOverride();
  if (override !== null && override.trim().length > 0) return override.trim();
  return getDefaultDirectorPlaybook();
}

/** Raw text to seed the editor textarea (override verbatim, else default). */
export function readDirectorPlaybookForEditor(): string {
  const override = readOverride();
  return override !== null ? override : getDefaultDirectorPlaybook();
}

/**
 * Save an edited playbook. Saving text identical to the default clears the
 * override so the panel reflects "default" again. Empty input also resets.
 */
export function writeDirectorPlaybook(text: string): void {
  const trimmed = text.trim();
  if (trimmed.length === 0 || trimmed === getDefaultDirectorPlaybook()) {
    resetDirectorPlaybook();
    return;
  }
  persist(trimmed);
  notify();
}

/** Drop the override and fall back to the shipped `director.md`. */
export function resetDirectorPlaybook(): void {
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // privacy-mode Safari / quota — ignore; nothing to remove.
    }
  }
  notify();
}

/** Subscribe to playbook changes (save/reset). Returns an unsubscribe fn. */
export function subscribeDirectorPlaybook(listener: PlaybookListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function readOverride(): string | null {
  if (typeof window === "undefined" || !window.localStorage) return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function persist(text: string): void {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, text);
  } catch {
    // Quota / privacy-mode — the edit applies for subscribers this session;
    // it just won't survive a reload.
  }
}

function notify(): void {
  const next = getEffectiveDirectorPlaybook();
  for (const listener of listeners) listener(next);
}
